import React, { useEffect, useRef, useState } from "react";
import IframeVideoEmbed from "./IframeVideoEmbed";
import VideoSkeletonLoader from "./VideoSkeletonLoader";
import { resolvePinterestPinId } from "../../utils/videoUrl";

declare global {
  interface Window {
    PinUtils?: { build: () => void };
  }
}

const PINIT_SCRIPT_ID = "pinterest-pinit-js";

function loadPinterestScript(): Promise<void> {
  return new Promise((resolve) => {
    if (document.getElementById(PINIT_SCRIPT_ID)) {
      window.PinUtils?.build();
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.id = PINIT_SCRIPT_ID;
    script.src = "https://assets.pinterest.com/js/pinit.js";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.PinUtils?.build();
      resolve();
    };
    script.onerror = () => {
      resolve();
    };
    document.body.appendChild(script);
  });
}

function hasVisibleEmbedContent(container: HTMLElement | null): boolean {
  if (!container) return false;

  return Boolean(
    container.querySelector("iframe, img, video, [data-pin-log]")
      ?.clientHeight,
  );
}

interface PinterestPinEmbedProps {
  url: string;
  pinId?: string;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  onError?: () => void;
}

const baseStyle: React.CSSProperties = {
  minHeight: "350px",
  border: "none",
  width: "100%",
};

const PinterestPinEmbed: React.FC<PinterestPinEmbedProps> = ({
  url,
  pinId: initialPinId,
  title,
  className = "w-100 h-100 rounded-4",
  style,
  onError,
}) => {
  const widgetRef = useRef<HTMLDivElement>(null);
  const [pinId, setPinId] = useState<string | undefined>(initialPinId);
  const [isResolving, setIsResolving] = useState(!initialPinId);
  const [useWidget, setUseWidget] = useState(false);
  const [isWidgetLoading, setIsWidgetLoading] = useState(true);

  useEffect(() => {
    if (initialPinId) {
      setPinId(initialPinId);
      setIsResolving(false);
      return;
    }

    let cancelled = false;

    resolvePinterestPinId(url).then((resolvedId) => {
      if (cancelled) return;

      if (resolvedId) {
        setPinId(resolvedId);
        setUseWidget(false);
      } else {
        setUseWidget(true);
        setIsWidgetLoading(true);
      }
      setIsResolving(false);
    });

    return () => {
      cancelled = true;
    };
  }, [url, initialPinId]);

  useEffect(() => {
    if (!useWidget || isResolving) return;

    let cancelled = false;
    let pollInterval: number | undefined;
    let errorTimeout: number | undefined;
    let buildTimeout: number | undefined;

    setIsWidgetLoading(true);

    loadPinterestScript().then(() => {
      if (cancelled) return;
      window.PinUtils?.build();
      buildTimeout = window.setTimeout(() => {
        if (cancelled) return;
        window.PinUtils?.build();

        pollInterval = window.setInterval(() => {
          if (cancelled) return;
          if (hasVisibleEmbedContent(widgetRef.current)) {
            setIsWidgetLoading(false);
            if (pollInterval) window.clearInterval(pollInterval);
            if (errorTimeout) window.clearTimeout(errorTimeout);
          }
        }, 300);

        errorTimeout = window.setTimeout(() => {
          if (pollInterval) window.clearInterval(pollInterval);
          if (cancelled || hasVisibleEmbedContent(widgetRef.current)) return;
          onError?.();
        }, 6000);
      }, 200);
    });

    return () => {
      cancelled = true;
      if (pollInterval) window.clearInterval(pollInterval);
      if (errorTimeout) window.clearTimeout(errorTimeout);
      if (buildTimeout) window.clearTimeout(buildTimeout);
    };
  }, [useWidget, isResolving, url, onError]);

  if (isResolving) {
    return <VideoSkeletonLoader className={className} style={style} />;
  }

  if (pinId && !useWidget) {
    return (
      <IframeVideoEmbed
        src={`https://assets.pinterest.com/ext/embed.html?id=${pinId}`}
        title={title}
        className={className}
        style={style}
        onError={onError}
      />
    );
  }

  return (
    <div
      className="position-relative w-100"
      style={{ ...baseStyle, ...style }}
    >
      {isWidgetLoading && (
        <VideoSkeletonLoader
          className={`${className} position-absolute top-0 start-0`}
          style={style}
        />
      )}
      <div
        ref={widgetRef}
        className={`${className} d-flex justify-content-center align-items-start overflow-hidden`}
        style={{
          ...baseStyle,
          ...style,
          opacity: isWidgetLoading ? 0 : 1,
          transition: "opacity 0.25s ease",
        }}
      >
        <a
          data-pin-do="embedPin"
          data-pin-width="large"
          href={url}
          style={{ maxWidth: "100%" }}
        />
      </div>
    </div>
  );
};

export default PinterestPinEmbed;
